import { z } from 'zod';
import {
  DurationMin,
  EventNotes,
  EventPlace,
  EventStatus,
  EventTitle,
  EventType,
  MapUrl,
  OCCURRENCE_FIELDS,
  RsvpAnswer,
  Weekday,
  Weekdays,
} from './events';
import { LocalDate, LocalTime } from './time';

// What the events API (B2; D12, D32, D33) takes and answers, for the API and the events screens
// (B3, the members'; B4, the admin's). A member's routes are under /api/events, an admin's under
// /api/admin/events and /api/admin/series. Every request that changes something is JSON, `{}`
// when it says nothing more (D21), and every refusal is `{ error: <code> }` (API_ERRORS, auth.ts):
// `not_found` for an unknown event, `event_cancelled` and `event_started` for an answer the event
// no longer takes or a series it can't be changed from, `invalid` for the rest. A date and a time
// are the club's clock's (Asia/Nicosia, time.ts); an instant is an ISO time in UTC.

/** A member who answered, as the other members see them: the name (D15). */
export const Attendee = z.object({ id: z.string(), displayName: z.string() });
export type Attendee = z.infer<typeof Attendee>;

/** How many active members said yes, and maybe: members never see the no's (D33). */
export const AnswerCounts = z.object({ yes: z.number().int(), maybe: z.number().int() });
export type AnswerCounts = z.infer<typeof AnswerCounts>;

/** An event as the lists and its page show it to a member (B3). */
export const EventView = z.object({
  id: z.string(),
  type: EventType,
  title: z.string(),
  /** The notes. */
  description: z.string().nullable(),
  place: z.string().nullable(),
  mapUrl: z.string().nullable(),
  /** When it starts: an instant, shown on the club's clock (`toLocal`). */
  startsAt: z.string(),
  durationMin: z.number().int(),
  /** A cancelled event stays in the lists, said to be cancelled. */
  status: EventStatus,
  /** The weekly series it's an occurrence of; null for a one-off. */
  seriesId: z.string().nullable(),
  counts: AnswerCounts,
  /** The signed-in member's own answer, or null. */
  myAnswer: RsvpAnswer.nullable(),
});
export type EventView = z.infer<typeof EventView>;

/** An event's page: the event, and who said yes and who said maybe, by name (D33). */
export const EventDetail = EventView.extend({
  answers: z.object({ yes: z.array(Attendee), maybe: z.array(Attendee) }),
});
export type EventDetail = z.infer<typeof EventDetail>;

/** `GET /api/events/upcoming`: every event not over yet, soonest first. */
export const EventsResponse = z.object({ events: z.array(EventView) });
export type EventsResponse = z.infer<typeof EventsResponse>;

/**
 * `GET /api/events/past[?before=<id>]`: the events that are over, newest first, a page at a time,
 * the next page before the last event of this one; `more` says whether there's one.
 */
export const PastEventsResponse = z.object({ events: z.array(EventView), more: z.boolean() });
export type PastEventsResponse = z.infer<typeof PastEventsResponse>;

/** `GET /api/events/:id`, and what an answer answers: the event's page. */
export const EventResponse = z.object({ event: EventDetail });
export type EventResponse = z.infer<typeof EventResponse>;

/**
 * `POST /api/events/:id/rsvp`: the member's answer, yes, no or maybe, which they can change until
 * the event starts (`event_started`); a cancelled event takes none (`event_cancelled`).
 */
export const RsvpRequest = z.object({ answer: RsvpAnswer });
export type RsvpRequest = z.infer<typeof RsvpRequest>;

// The admin's side (B4's forms), under /api/admin, for admins only.

/** A weekly series: its template and its rule, as the admin's forms show them. */
export const SeriesView = z.object({
  id: z.string(),
  type: EventType,
  title: z.string(),
  description: z.string().nullable(),
  place: z.string().nullable(),
  mapUrl: z.string().nullable(),
  durationMin: z.number().int(),
  /** ISO's days: 1 Monday to 7 Sunday, in order. */
  weekdays: z.array(Weekday),
  startTime: LocalTime,
  /** The first date its days apply from, and the last it can meet on (included), or none. */
  fromDate: LocalDate,
  untilDate: LocalDate.nullable(),
});
export type SeriesView = z.infer<typeof SeriesView>;

/** A member who answered, as an admin sees them: the username too (D15). */
export const AdminAttendee = Attendee.extend({ username: z.string() });
export type AdminAttendee = z.infer<typeof AdminAttendee>;

/**
 * An event as the admin's forms see it: its fields, its date in its series and the fields it has
 * of its own (D32), its series, and every answer, the no's too.
 */
export const AdminEventDetail = EventView.omit({ counts: true, myAnswer: true }).extend({
  seriesDate: LocalDate.nullable(),
  overrides: z.array(z.enum(OCCURRENCE_FIELDS)),
  series: SeriesView.nullable(),
  answers: z.object({
    yes: z.array(AdminAttendee),
    maybe: z.array(AdminAttendee),
    no: z.array(AdminAttendee),
  }),
});
export type AdminEventDetail = z.infer<typeof AdminEventDetail>;

/** `GET /api/admin/events/:id`, and what a one-off's creation (201) and an edit answer. */
export const AdminEventResponse = z.object({ event: AdminEventDetail });
export type AdminEventResponse = z.infer<typeof AdminEventResponse>;

/** The fields a new one-off or series is made with: an empty place, note or link is none. */
const template = {
  type: EventType,
  title: EventTitle,
  description: EventNotes.default(null),
  place: EventPlace.default(null),
  mapUrl: MapUrl.default(null),
  durationMin: DurationMin,
};

/** `POST /api/admin/events`: a one-off, on a date at a time on the club's clock. */
export const CreateEventRequest = z.object({ ...template, date: LocalDate, time: LocalTime });
export type CreateEventRequest = z.infer<typeof CreateEventRequest>;

/**
 * `POST /api/admin/series`: a weekly series, its days, its time on the club's clock, its first
 * date, and its last or none (open until changed). One that would never meet again is `invalid`.
 */
export const CreateSeriesRequest = z
  .object({
    ...template,
    weekdays: Weekdays,
    startTime: LocalTime,
    fromDate: LocalDate,
    untilDate: LocalDate.nullable().default(null),
  })
  .refine(
    (rule) => rule.untilDate === null || rule.untilDate >= rule.fromDate,
    'ends before it starts',
  );
export type CreateSeriesRequest = z.infer<typeof CreateSeriesRequest>;

/** What creating a series answers (201): the series, and how many of its events it made. */
export const CreatedSeriesResponse = z.object({ series: SeriesView, added: z.number().int() });
export type CreatedSeriesResponse = z.infer<typeof CreatedSeriesResponse>;

const somethingGiven = (edit: Record<string, unknown>) =>
  Object.values(edit).some((value) => value !== undefined);

/**
 * `PATCH /api/admin/events/:id`, "this one": any of its fields, at least one; null, or an empty
 * text, clears the place, the notes or the link. A new date and time, given together, move it
 * (an occurrence keeps its date in its series); the status cancels it, or takes that back. An
 * occurrence's field that then differs from its series' is its own (D32).
 */
export const EditEventRequest = z
  .object({
    type: EventType.optional(),
    title: EventTitle.optional(),
    description: EventNotes.optional(),
    place: EventPlace.optional(),
    mapUrl: MapUrl.optional(),
    durationMin: DurationMin.optional(),
    date: LocalDate.optional(),
    time: LocalTime.optional(),
    status: EventStatus.optional(),
  })
  .refine((edit) => (edit.date === undefined) === (edit.time === undefined), 'date and time')
  .refine(somethingGiven, 'nothing to change');
export type EditEventRequest = z.infer<typeof EditEventRequest>;

/**
 * `PATCH /api/admin/events/:id/following`, "this and the following", from an occurrence still to
 * come: any of the template's fields, the time, the days and the last date (null: none), at least
 * one (D32's rules). The last date the series has already is no change, and so are the days it has
 * already, sent from a date its rule covers; sent from a date before its rule's first date, those
 * days apply from there, as new days do (D33). A rule that wouldn't hold, a new last date before
 * this event's date, or a date the series can't be changed from (D32), is `invalid`.
 */
export const ChangeSeriesRequest = z
  .object({
    type: EventType.optional(),
    title: EventTitle.optional(),
    description: EventNotes.optional(),
    place: EventPlace.optional(),
    mapUrl: MapUrl.optional(),
    durationMin: DurationMin.optional(),
    weekdays: Weekdays.optional(),
    startTime: LocalTime.optional(),
    untilDate: LocalDate.nullable().optional(),
  })
  .refine(somethingGiven, 'nothing to change');
export type ChangeSeriesRequest = z.infer<typeof ChangeSeriesRequest>;

/**
 * What a change of the following answers: the series as it is now; this event as it is now, or
 * null when the change removed it (new days without its own); and how many of the following
 * events took the change, were removed with their answers, were kept as exceptions, were made.
 */
export const ChangedSeriesResponse = z.object({
  series: SeriesView,
  event: AdminEventDetail.nullable(),
  updated: z.number().int(),
  removed: z.number().int(),
  kept: z.number().int(),
  added: z.number().int(),
});
export type ChangedSeriesResponse = z.infer<typeof ChangedSeriesResponse>;

/**
 * What `POST /api/admin/events/:id/following/cancel` (`{}`) answers, "cancel this and the
 * following" (the owner's answer, 2026-10-18; D33): this event and every later one removed with
 * their answers, the series ending the day before; null when it went whole.
 */
export const CancelledSeriesResponse = z.object({
  series: SeriesView.nullable(),
  removed: z.number().int(),
});
export type CancelledSeriesResponse = z.infer<typeof CancelledSeriesResponse>;
